import { useCallback } from "react"
import { useSearchParams } from "react-router"

const SIGNAL_PARAM = "signal"
const GROUP_PARAM = "group"

/**
 * Single source of truth for the one open detail view, tracked in the URL.
 *
 * A signal detail (`?signal=<id>`) and a map-group detail (`?group=<lat,lon>`)
 * are mutually exclusive: only one detail view is ever open at a time. Every
 * mutation rewrites both params in a single history update, so the two can
 * never coexist (and one setter can't clobber the other). State is shareable
 * and survives reload. Opening/closing a detail view uses `replace`, so the
 * transient overlay does not push browser-history entries (Back returns to the
 * route you arrived from, not through each drawer toggle).
 */
export const useSelectedDetailParam = (): {
  selectedId: string | null
  selectedGroupKey: string | null
  openSignal: (id: string) => void
  openGroup: (key: string) => void
  close: () => void
} => {
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedId = searchParams.get(SIGNAL_PARAM)
  const selectedGroupKey = searchParams.get(GROUP_PARAM)

  const write = useCallback(
    (next: { signal: string } | { group: string } | null) => {
      setSearchParams(
        prev => {
          const params = new URLSearchParams(prev)
          // Clear both first so exactly one detail view is ever encoded.
          params.delete(SIGNAL_PARAM)
          params.delete(GROUP_PARAM)
          if (next && "signal" in next) params.set(SIGNAL_PARAM, next.signal)
          else if (next && "group" in next) params.set(GROUP_PARAM, next.group)
          return params
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const openSignal = useCallback((id: string) => write({ signal: id }), [write])
  const openGroup = useCallback((key: string) => write({ group: key }), [write])
  const close = useCallback(() => write(null), [write])

  return { selectedId, selectedGroupKey, openSignal, openGroup, close }
}
